import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Lock as LockIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  Backup as BackupIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import axiosInstance from '../utils/axiosConfig';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import BackupTab from '../components/settings/BackupTab';
import GeneralSettingsTab from '../components/settings/GeneralSettingsTab';
import { usePermissions } from '../hooks/usePermissions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface FiscalYear {
  _id: string;
  yearName: string;
  startDate: string;
  endDate: string | null;
  databaseName: string;
  isCurrent: boolean;
}

const AdvancedSettingsPage: React.FC = () => {
  // التحقق من الصلاحيات
  const { hasPermission } = usePermissions();
  const canAccessPage = hasPermission('settings', 'advanced-settings', 'view_page');
  const canAccessFiscalCloseTab = hasPermission('settings', 'advanced-settings', 'access_fiscal_close_tab');
  const canCloseFiscalYear = hasPermission('settings', 'advanced-settings', 'close_fiscal_year');
  const canAccessFiscalManagementTab = hasPermission('settings', 'advanced-settings', 'access_fiscal_management_tab');
  const canActivateDatabase = hasPermission('settings', 'advanced-settings', 'activate_database');
  const canAccessBackupTab = hasPermission('settings', 'advanced-settings', 'access_backup_tab');
  const canAccessGeneralSettingsTab = hasPermission('settings', 'advanced-settings', 'access_general_settings_tab');
  const canDeleteFiscalYear = hasPermission('settings', 'advanced-settings', 'delete_fiscal_year');

  const [tabValue, setTabValue] = useState(0);
  const [currentFiscalYear, setCurrentFiscalYear] = useState<FiscalYear | null>(null);
  const [lastClosedYear, setLastClosedYear] = useState<FiscalYear | null>(null);
  const [allFiscalYears, setAllFiscalYears] = useState<FiscalYear[]>([]);
  const [activeDatabase, setActiveDatabase] = useState<string>('');
  const [availableDatabases, setAvailableDatabases] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const [closingDate, setClosingDate] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [nextYearName, setNextYearName] = useState('');
  const [closingInProgress, setClosingInProgress] = useState(false);
  const [activatingYear, setActivatingYear] = useState<string | null>(null);
  const [transferData, setTransferData] = useState(true); // true = نقل كل البيانات, false = المستخدمين فقط
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fiscalYearToDelete, setFiscalYearToDelete] = useState<FiscalYear | null>(null);
  const [deletingYear, setDeletingYear] = useState(false);

  useEffect(() => {
    // فقط نجلب البيانات عند فتح التبويب الأول (إقفال السنة المالية)
    if (canAccessFiscalCloseTab && (tabValue === 0 || !canAccessFiscalManagementTab)) {
      fetchCurrentFiscalYear();
    }
    
    // جلب جميع السنوات المالية عند فتح التبويب الثاني (إدارة السنوات المالية)
    const managementTabIndex = canAccessFiscalCloseTab ? 1 : 0;
    if (canAccessFiscalManagementTab && tabValue === managementTabIndex) {
      fetchAllFiscalYears();
    }
  }, [tabValue, canAccessFiscalCloseTab, canAccessFiscalManagementTab]);

  const fetchCurrentFiscalYear = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/settings/fiscal-year/current');
      setCurrentFiscalYear(response.data.data);
      setLastClosedYear(response.data.lastClosedYear || null);
    } catch (err: any) {
      // لا نعرض خطأ إذا لم توجد سنة مالية (404) - هذا طبيعي
      if (err.response?.status === 404) {
        // حتى لو لم توجد سنة حالية، نحاول جلب آخر سنة مقفلة من الـ response
        setLastClosedYear(err.response?.data?.lastClosedYear || null);
        setCurrentFiscalYear(null);
        // لا نطبع خطأ في console لأن 404 طبيعي عندما لا توجد سنة مالية نشطة
      } else {
        // خطأ حقيقي غير 404
        console.error('Error fetching fiscal year:', err);
        setError(err.response?.data?.message || 'حدث خطأ في تحميل بيانات السنة المالية');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFiscalYears = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/settings/fiscal-year/all');
      setAllFiscalYears(response.data.data.fiscalYears);
      setActiveDatabase(response.data.data.activeDatabase);
      setAvailableDatabases(response.data.data.availableDatabases || []);
    } catch (err: any) {
      console.error('Error fetching all fiscal years:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل السنوات المالية');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleActivateFiscalYear = async (fiscalYearId: string) => {
    try {
      setActivatingYear(fiscalYearId);
      setError(null);
      setSuccess(null);

      const response = await axiosInstance.post('/settings/fiscal-year/activate', {
        fiscalYearId
      });

      setSuccess(response.data.message);
      
      // إعادة تحميل البيانات
      await fetchAllFiscalYears();
      await fetchCurrentFiscalYear();

      // إشعار المستخدم بإعادة تحميل الصفحة
      if (response.data.data.requiresReload) {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error activating fiscal year:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء تفعيل السنة المالية');
    } finally {
      setActivatingYear(null);
    }
  };

  const handleActivateDatabase = async (databaseName: string) => {
    try {
      setActivatingYear(databaseName);
      setError(null);
      setSuccess(null);

      const response = await axiosInstance.post('/settings/fiscal-year/activate-database', {
        databaseName
      });

      setSuccess(response.data.message);
      
      // إعادة تحميل الصفحة بعد 3 ثواني (لإعطاء الوقت للسيرفر لإعادة التشغيل)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (err: any) {
      console.error('Error activating database:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء تفعيل قاعدة البيانات');
      setActivatingYear(null);
    }
  };

  const handleOpenCloseDialog = () => {
    // Set default closing date to today
    const today = new Date().toISOString().split('T')[0];
    setClosingDate(today);
    
    // Set default year names
    const currentYear = new Date().getFullYear();
    setCurrentYearName(currentYear.toString());
    setNextYearName((currentYear + 1).toString());
    
    setOpenCloseDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenCloseDialog(false);
    setClosingDate('');
    setCurrentYearName('');
    setNextYearName('');
    setTransferData(true);
    setError(null);
  };

  const handleCloseFiscalYear = async () => {
    if (!closingDate) {
      setError('يرجى إدخال تاريخ الإقفال');
      return;
    }

    if (!currentYearName || !nextYearName) {
      setError('يرجى إدخال اسم السنة المالية الحالية والقادمة');
      return;
    }

    try {
      setClosingInProgress(true);
      setError(null);
      setSuccess(null);

      const response = await axiosInstance.post('/settings/fiscal-year/close', {
        closingDate,
        currentYearName,
        nextYearName,
        transferData
      });

      setSuccess(response.data.message || 'تم إقفال السنة المالية بنجاح');
      setOpenCloseDialog(false);
      
      // Refresh fiscal year data
      await fetchCurrentFiscalYear();
    } catch (err: any) {
      console.error('Error closing fiscal year:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء إقفال السنة المالية');
    } finally {
      setClosingInProgress(false);
    }
  };

  const handleOpenDeleteDialog = (fiscalYear: FiscalYear) => {
    setFiscalYearToDelete(fiscalYear);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setFiscalYearToDelete(null);
  };

  const handleDeleteFiscalYear = async () => {
    if (!fiscalYearToDelete) return;

    try {
      setDeletingYear(true);
      setError(null);
      setSuccess(null);

      await axiosInstance.delete(`/settings/fiscal-year/${fiscalYearToDelete._id}`);

      setSuccess(`تم حذف سجل السنة المالية "${fiscalYearToDelete.yearName}" بنجاح`);
      setDeleteDialogOpen(false);
      setFiscalYearToDelete(null);
      
      // إعادة تحميل البيانات
      await fetchCurrentFiscalYear();
      await fetchAllFiscalYears();
    } catch (err: any) {
      console.error('Error deleting fiscal year:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء حذف السنة المالية');
    } finally {
      setDeletingYear(false);
    }
  };

  // التحقق من صلاحية الوصول للصفحة
  if (!canAccessPage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" icon={<LockIcon />}>
          <Typography variant="h6" gutterBottom>
            ليس لديك صلاحية للوصول إلى هذه الصفحة
          </Typography>
          <Typography variant="body2">
            يرجى التواصل مع مدير النظام للحصول على الصلاحيات اللازمة.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // حساب التبويبات المتاحة بناءً على الصلاحيات
  const availableTabs = [
    canAccessFiscalCloseTab,
    canAccessFiscalManagementTab,
    canAccessBackupTab,
    canAccessGeneralSettingsTab
  ];
  
  // إذا لم يكن لديه صلاحية لأي تبويب
  if (!availableTabs.some(tab => tab)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          الإعدادات المتقدمة
        </Typography>
        <Alert severity="warning">
          <Typography variant="body1">
            ليس لديك صلاحية للوصول إلى أي من التبويبات في هذه الصفحة.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        الإعدادات المتقدمة
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="advanced settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {canAccessFiscalCloseTab && (
            <Tab label="إقفال السنة المالية" icon={<LockIcon />} iconPosition="start" />
          )}
          {canAccessFiscalManagementTab && (
            <Tab label="إدارة السنوات المالية" icon={<StorageIcon />} iconPosition="start" />
          )}
          {canAccessBackupTab && (
            <Tab label="النسخ الاحتياطي" icon={<BackupIcon />} iconPosition="start" />
          )}
          {canAccessGeneralSettingsTab && (
            <Tab label="الإعدادات العامة" icon={<SettingsIcon />} iconPosition="start" />
          )}
        </Tabs>

        {canAccessFiscalCloseTab && (
          <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                إدارة السنة المالية
              </Typography>

              {currentFiscalYear ? (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          السنة المالية الحالية
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {currentFiscalYear.yearName}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box sx={{ display: 'flex', gap: 4 }}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            تاريخ البداية
                          </Typography>
                          <Typography variant="body1">
                            {format(new Date(currentFiscalYear.startDate), 'dd/MM/yyyy', { locale: ar })}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            تاريخ النهاية
                          </Typography>
                          <Typography variant="body1">
                            {currentFiscalYear.endDate ? format(new Date(currentFiscalYear.endDate), 'dd/MM/yyyy', { locale: ar }) : 'مفتوحة'}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {lastClosedYear && (
                    <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              آخر سنة مالية تم إقفالها
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {lastClosedYear.yearName}
                            </Typography>
                          </Box>

                          <Divider />

                          <Box sx={{ display: 'flex', gap: 4 }}>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">
                                تاريخ البداية
                              </Typography>
                              <Typography variant="body1">
                                {format(new Date(lastClosedYear.startDate), 'dd/MM/yyyy', { locale: ar })}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">
                                تاريخ الإقفال
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                {lastClosedYear.endDate ? format(new Date(lastClosedYear.endDate), 'dd/MM/yyyy', { locale: ar }) : '-'}
                              </Typography>
                            </Box>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {canCloseFiscalYear && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<LockIcon />}
                  onClick={handleOpenCloseDialog}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  إقفال السنة المالية
                </Button>
              )}
              {!canCloseFiscalYear && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  ليس لديك صلاحية إقفال السنة المالية
                </Alert>
              )}
              
              {/* زر حذف السنة المالية - يظهر للسنة الحالية أو آخر سنة مقفلة */}
              {(currentFiscalYear || lastClosedYear) && canDeleteFiscalYear && (
                <Button
                  variant="outlined"
                  color="error"
                  size="large"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleOpenDeleteDialog(currentFiscalYear || lastClosedYear!)}
                  fullWidth
                >
                  حذف سجل السنة المالية
                </Button>
              )}
              
              {(currentFiscalYear || lastClosedYear) && !canDeleteFiscalYear && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  ليس لديك صلاحية حذف السنة المالية
                </Alert>
              )}
            </Box>
          )}
          </TabPanel>
        )}

        {canAccessFiscalManagementTab && (
          <TabPanel value={tabValue} index={canAccessFiscalCloseTab ? 1 : 0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  إدارة السنوات المالية
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchAllFiscalYears}
                  size="small"
                >
                  تحديث
                </Button>
              </Box>

              {activeDatabase && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    قاعدة البيانات النشطة حالياً:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {activeDatabase}
                  </Typography>
                </Alert>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>قاعدة البيانات</strong></TableCell>
                      <TableCell><strong>الحالة</strong></TableCell>
                      <TableCell align="center"><strong>الإجراءات</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableDatabases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            لا توجد قواعد بيانات متاحة
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      availableDatabases.map((dbName) => {
                        const isActive = dbName === activeDatabase;
                        const fiscalYear = allFiscalYears.find(y => y.databaseName === dbName);
                        
                        return (
                          <TableRow
                            key={dbName}
                            sx={{
                              bgcolor: isActive ? 'success.50' : 'inherit',
                              '&:hover': { bgcolor: isActive ? 'success.100' : 'grey.50' }
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: isActive ? 'bold' : 'normal' }}>
                                  {dbName}
                                </Typography>
                                {fiscalYear && (
                                  <Chip
                                    label={fiscalYear.yearName}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {isActive ? (
                                <Chip
                                  label="نشطة"
                                  size="small"
                                  color="success"
                                  icon={<CheckCircleIcon />}
                                />
                              ) : (
                                <Chip
                                  label="متاحة"
                                  size="small"
                                  color="default"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {!isActive && canActivateDatabase && (
                                <Tooltip title="تفعيل هذه قاعدة البيانات">
                                  <span>
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      onClick={() => handleActivateDatabase(dbName)}
                                      disabled={activatingYear === dbName}
                                    >
                                      {activatingYear === dbName ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <PlayArrowIcon />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {!isActive && !canActivateDatabase && (
                                <Tooltip title="ليس لديك صلاحية تفعيل قاعدة البيانات">
                                  <span>
                                    <IconButton
                                      color="default"
                                      size="small"
                                      disabled
                                    >
                                      <LockIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ملاحظة هامة
                </Typography>
                <Typography variant="body2">
                  عند تفعيل سنة مالية مختلفة، سيتم تحويل جميع المستخدمين للعمل على قاعدة البيانات الجديدة. 
                  سيتم إعادة تحميل الصفحة تلقائياً بعد التفعيل.
                </Typography>
              </Alert>
            </Box>
          )}
          </TabPanel>
        )}
        
        {canAccessBackupTab && (
          <TabPanel value={tabValue} index={(canAccessFiscalCloseTab ? 1 : 0) + (canAccessFiscalManagementTab ? 1 : 0)}>
            <BackupTab />
          </TabPanel>
        )}
        
        {canAccessGeneralSettingsTab && (
          <TabPanel value={tabValue} index={(canAccessFiscalCloseTab ? 1 : 0) + (canAccessFiscalManagementTab ? 1 : 0) + (canAccessBackupTab ? 1 : 0)}>
            <GeneralSettingsTab />
          </TabPanel>
        )}
      </Paper>

      {/* Close Fiscal Year Dialog */}
      <Dialog
        open={openCloseDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon color="primary" />
            <Typography variant="h6">إقفال السنة المالية</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Alert severity="warning">
              هذه العملية لا يمكن التراجع عنها. يرجى التأكد من صحة جميع البيانات قبل المتابعة.
            </Alert>

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                نوع الإقفال
              </FormLabel>
              <RadioGroup
                value={transferData ? 'full' : 'users-only'}
                onChange={(e) => setTransferData(e.target.value === 'full')}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        إقفال مع نقل جميع البيانات
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        سيتم نقل العملاء، المنتجات، الحسابات، المخزون، وجميع البيانات الأساسية
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="users-only"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        إقفال مع نقل المستخدمين فقط
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        قاعدة بيانات جديدة تماماً - سيتم نقل المستخدمين فقط بدون أي بيانات أخرى
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Divider />

            <TextField
              label="السنة المالية الحالية"
              type="text"
              value={currentYearName}
              onChange={(e) => setCurrentYearName(e.target.value)}
              fullWidth
              required
              placeholder="مثال: 2025"
              helperText="أدخل اسم السنة المالية الحالية التي سيتم إقفالها"
            />

            <TextField
              label="السنة المالية القادمة"
              type="text"
              value={nextYearName}
              onChange={(e) => setNextYearName(e.target.value)}
              fullWidth
              required
              placeholder="مثال: 2026"
              helperText="أدخل اسم السنة المالية الجديدة التي سيتم إنشاؤها"
            />

            <TextField
              label="تاريخ الإقفال"
              type="date"
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
              helperText="حدد التاريخ الذي سيتم فيه إقفال السنة المالية"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={closingInProgress}>
            إلغاء
          </Button>
          <Button
            onClick={handleCloseFiscalYear}
            variant="contained"
            color="primary"
            disabled={closingInProgress || !closingDate || !currentYearName || !nextYearName}
            startIcon={closingInProgress ? <CircularProgress size={20} /> : <LockIcon />}
          >
            {closingInProgress ? 'جاري الإقفال...' : 'تأكيد الإقفال'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Fiscal Year Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6">تأكيد حذف السنة المالية</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                تحذير: هذه العملية لا يمكن التراجع عنها!
              </Typography>
              <Typography variant="body2">
                سيتم حذف سجل السنة المالية من قاعدة البيانات فقط. لن يتم حذف قاعدة البيانات نفسها.
              </Typography>
            </Alert>

            {fiscalYearToDelete && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  السنة المالية المراد حذفها:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {fiscalYearToDelete.yearName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  قاعدة البيانات: {fiscalYearToDelete.databaseName}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              هل أنت متأكد من رغبتك في حذف هذا السجل؟
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deletingYear}>
            إلغاء
          </Button>
          <Button
            onClick={handleDeleteFiscalYear}
            variant="contained"
            color="error"
            disabled={deletingYear}
            startIcon={deletingYear ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingYear ? 'جاري الحذف...' : 'تأكيد الحذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedSettingsPage;